import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { assetsApi, maintenanceApi, taskLibraryApi } from '../../services/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;
const today = () => new Date().toISOString().slice(0, 10);

const BulkShell = ({ open, onOpenChange, title, count, busy, onSubmit, submitLabel, testid, children }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md" data-testid={`${testid}-dialog`}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>Applies to {count} selected asset{count === 1 ? '' : 's'}.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">{children}</div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>Cancel</Button>
        <Button onClick={onSubmit} disabled={busy} className="btn-industrial" data-testid={`${testid}-submit`}>
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{submitLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const BulkDepotDialog = ({ open, onOpenChange, ids, depots, onDone }) => {
  const [depotId, setDepotId] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!depotId) { toast.error('Choose a depot'); return; }
    setBusy(true);
    try {
      const { data } = await assetsApi.bulkDepot(ids, depotId);
      toast.success(`Moved ${data.moved_count} asset(s)${data.unchanged_count ? `, ${data.unchanged_count} already there` : ''}`);
      onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not change depot')); } finally { setBusy(false); }
  };
  return (
    <BulkShell open={open} onOpenChange={onOpenChange} title="Change depot" count={ids.length}
      busy={busy} onSubmit={submit} submitLabel="Move assets" testid="bulk-depot">
      <div className="space-y-1.5">
        <Label>New depot</Label>
        <Select value={depotId} onValueChange={setDepotId}>
          <SelectTrigger data-testid="bulk-depot-select"><SelectValue placeholder="Select depot" /></SelectTrigger>
          <SelectContent>
            {depots.map(d => <SelectItem key={d.id} value={d.id} data-testid={`bulk-depot-option-${d.id}`}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </BulkShell>
  );
};

export const BulkReadinessDialog = ({ open, onOpenChange, ids, onDone }) => {
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);
  const isAuto = status === 'auto';
  const submit = async () => {
    if (!status) { toast.error('Choose a readiness status'); return; }
    if (!isAuto && !reason.trim()) { toast.error('Enter a reason'); return; }
    setBusy(true);
    try {
      const expires_at = !isAuto && expires ? new Date(`${expires}T23:59:59`).toISOString() : null;
      const { data } = await assetsApi.bulkReadiness({ asset_ids: ids, status, reason: reason.trim(), expires_at });
      toast.success(isAuto ? `Override cleared on ${data.updated_count} asset(s)` : `Readiness set on ${data.updated_count} asset(s)`);
      onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not change readiness')); } finally { setBusy(false); }
  };
  return (
    <BulkShell open={open} onOpenChange={onOpenChange} title="Change readiness" count={ids.length}
      busy={busy} onSubmit={submit} submitLabel={isAuto ? 'Clear override' : 'Apply override'} testid="bulk-readiness">
      <div className="space-y-1.5">
        <Label>Readiness</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="bulk-readiness-status"><SelectValue placeholder="Select status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="green" data-testid="bulk-readiness-green">Ready</SelectItem>
            <SelectItem value="amber" data-testid="bulk-readiness-amber">Attention</SelectItem>
            <SelectItem value="red" data-testid="bulk-readiness-red">Not ready</SelectItem>
            <SelectItem value="auto" data-testid="bulk-readiness-auto">Automatic (clear override)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {!isAuto && (
        <>
          <div className="space-y-1.5">
            <Label>Reason (logged)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="e.g. Certificates renewed, paperwork pending" data-testid="bulk-readiness-reason" />
          </div>
          <div className="space-y-1.5">
            <Label>Override expires (optional)</Label>
            <Input type="date" min={today()} value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="bulk-readiness-expires" />
            <p className="text-xs text-muted-foreground">After this date the calculated status returns automatically. Quarantined assets stay Not ready.</p>
          </div>
        </>
      )}
    </BulkShell>
  );
};

export const BulkScheduleDialog = ({ open, onOpenChange, assets, onDone }) => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [intervalType, setIntervalType] = useState('time_based');
  const [intervalVal, setIntervalVal] = useState('');
  const [lastDate, setLastDate] = useState(today());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) taskLibraryApi.list().then(r => setTemplates(r.data || [])).catch(() => toast.error('Could not load templates'));
  }, [open]);
  const pickTemplate = (id) => {
    setTemplateId(id);
    setName(templates.find(t => t.id === id)?.name || '');
  };
  const submit = async () => {
    const n = Number(intervalVal);
    if (!templateId) { toast.error('Choose a template'); return; }
    if (!name.trim()) { toast.error('Enter a schedule name'); return; }
    if (!n || n <= 0) { toast.error('Enter a valid interval'); return; }
    if (intervalType === 'time_based' && !lastDate) { toast.error('Enter the last service date'); return; }
    setBusy(true);
    try {
      const timeBased = intervalType === 'time_based';
      const { data } = await maintenanceApi.bulkCreate({
        name: name.trim(), interval_type: intervalType, task_template_id: templateId,
        interval_days: timeBased ? n : null, interval_hours: timeBased ? null : n,
        targets: assets.map(a => timeBased
          ? { asset_id: a.id, last_service_date: new Date(`${lastDate}T00:00:00`).toISOString() }
          : { asset_id: a.id, last_service_hours: Number(a.hours) || 0 }),
      });
      toast.success(`Created ${data.created_count} schedule(s)${data.skipped_count ? `, skipped ${data.skipped_count} that already have "${name.trim()}"` : ''}`);
      onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not create schedules')); } finally { setBusy(false); }
  };
  return (
    <BulkShell open={open} onOpenChange={onOpenChange} title="Schedule service" count={assets.length}
      busy={busy} onSubmit={submit} submitLabel="Create schedules" testid="bulk-schedule">
      <div className="space-y-1.5">
        <Label>Task template</Label>
        <Select value={templateId} onValueChange={pickTemplate}>
          <SelectTrigger data-testid="bulk-schedule-template"><SelectValue placeholder={templates.length ? 'Select template' : 'No templates in Task Library'} /></SelectTrigger>
          <SelectContent>
            {templates.map(t => <SelectItem key={t.id} value={t.id} data-testid={`bulk-schedule-template-${t.id}`}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>Schedule name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="bulk-schedule-name" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Interval type</Label>
          <Select value={intervalType} onValueChange={setIntervalType}>
            <SelectTrigger data-testid="bulk-schedule-interval-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="time_based">Time (days)</SelectItem>
              <SelectItem value="usage_based">Usage (hours)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Every ({intervalType === 'time_based' ? 'days' : 'hours'})</Label>
          <Input type="number" min="1" value={intervalVal} onChange={(e) => setIntervalVal(e.target.value)} data-testid="bulk-schedule-interval" />
        </div>
      </div>
      {intervalType === 'time_based' ? (
        <div className="space-y-1.5">
          <Label>Last service date (all selected)</Label>
          <Input type="date" max={today()} value={lastDate} onChange={(e) => setLastDate(e.target.value)} data-testid="bulk-schedule-last-date" />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="bulk-schedule-hours-note">Each asset's current meter reading is used as its last-service hours.</p>
      )}
    </BulkShell>
  );
};

const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const exportAssetsCsv = (assets, columns, valueFor) => {
  const READINESS = { green: 'Ready', amber: 'Attention', red: 'Not ready', grey: 'Not assessed' };
  const header = [...columns.map(c => c.label), 'Readiness', 'Readiness reasons'];
  const rows = assets.map(a => [
    ...columns.map(c => valueFor(a, c.key)),
    READINESS[a.readiness?.status] || 'Not assessed',
    (a.readiness?.reasons || []).join('; '),
  ]);
  const csv = [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `assets-selection-${today()}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
